import { FC, useMemo, useState } from "react";
import { format } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { isEmpty } from "lodash";
import { Alert } from "@mui/material";
import {
  DateRangeCalendar,
  LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import __ from "src/utils/Translations";
import { useFetchEmployeeAbsences } from "src/Pages/Company/Absences/api/employee.queries";
import getMonthRange from "src/utils/Company/Absences/getMonthRange";
import useTransformAbsences from "src/utils/hooks/absences/useTransformAbsences";
import Loader from "src/Pages/Company/Absences/Loader";
import CustomCalendarDay from "src/Pages/Company/Absences/Calendars/Calendar/CalendarPreview/CustomCalendarPreviewDay";
import {
  IAbsenceRequestDay,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { datepickerStyles } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker/Datepicker.styles";

interface IAbsenceRequestCalendarPreviewProps {
  employeeId: string;
}

const AbsenceRequestCalendarPreview: FC<
  IAbsenceRequestCalendarPreviewProps
> = ({ employeeId }) => {
  const currentYear = new Date().getFullYear().toString();
  const [displayYear, setDisplayYear] = useState(currentYear);
  const [displayMonths, setDisplayMonths] = useState(getMonthRange(new Date()));

  const [transformAbsences] = useTransformAbsences();

  const {
    data: absencesData,
    isFetching: isFetchingAbsences,
    isError: isErrorAbsences,
  } = useFetchEmployeeAbsences({
    employeeId,
    ...displayMonths,
    statuses: [
      Status.WAITING_FOR_APPROVAL,
      Status.ACCEPTED,
      Status.CORRECTION_REQUIRED,
    ],
  });

  const [absenceRequestDays] = useMemo<[IAbsenceRequestDay[], Date[]]>(() => {
    if (!absencesData) return [[], []];

    return transformAbsences(absencesData);
  }, [absencesData, transformAbsences]);

  const excludedDays = useMemo(() => {
    if (!absencesData) return [];

    return absencesData.calendarInfos.details[displayYear]?.excludedDays || [];
  }, [absencesData, displayYear]);

  const handleMonthChange = (date: Date) => {
    const yearString = format(date, "y");

    setDisplayMonths(getMonthRange(date));

    if (yearString !== displayYear) {
      setDisplayYear(yearString);
    }
  };

  return (
    <>
      {isErrorAbsences ? (
        <Alert severity="error" sx={{ padding: "0.5rem", mb: "1rem" }}>
          {__("Błąd pobierania danych.")}
        </Alert>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
          <DateRangeCalendar
            views={["day"]}
            readOnly
            reduceAnimations
            calendars={2}
            loading={isFetchingAbsences}
            dayOfWeekFormatter={(date: Date) =>
              date.toLocaleString("pl", { weekday: "short" })
            }
            disablePast={!absencesData?.isEntitledToEmployeeCalendarView}
            showDaysOutsideCurrentMonth={false}
            onMonthChange={handleMonthChange}
            renderLoading={() => <Loader />}
            slots={{
              day: (props) =>
                CustomCalendarDay({
                  ...props,
                  absenceRequestDays,
                  excludedDays,
                  plannedDays: absencesData?.absencePlans || [],
                  disabled: isEmpty(absencesData?.calendarInfos),
                }),
            }}
            sx={{
              ...datepickerStyles,
              ".MuiPickersArrowSwitcher-previousIconButton.Mui-disabled": {
                visibility: "hidden",
              },
            }}
          />
        </LocalizationProvider>
      )}
    </>
  );
};

export default AbsenceRequestCalendarPreview;
